// Client for /api/admin — the Configurations module.
// Mirrors backend/chainlit/admin_api.py. Every permission decision is made
// server-side; this file only shapes what the UI offers.

export type AdminRole = 'SUPER_ADMIN' | 'PARTNER_ADMIN';

export interface AdminMe {
  email: string;
  role: AdminRole;
  scope: number[] | null; // null = every customer
  sees_cost: boolean;
  grantable_features: string[];
  // Whether to show the Platform section. Hiding a control the caller cannot
  // use is kinder than letting them fill in a form that 403s.
  can_manage_platform?: boolean;
}

export interface PlatformSetting {
  key: string;
  value: number;
  default: number;
  min: number;
  max: number;
  // 'immediately' | 'new containers' -- the second one matters: Docker cannot
  // change a running container's limits, and an operator who does not know
  // that will restart one to make a change take effect.
  applies: string;
  description: string;
  is_default: boolean;
}

export interface CustomerRow {
  pk: number;
  name: string;
  max_message_limit: number | null;
  max_users: number | null;
  user_count: number;
  disabled_count: number;
  flag_count: number;
  entitlement_count: number;
}

export interface Flag {
  feature: string;
  user_id: string | null; // null = tenant-wide
  enabled: boolean;
  enabled_by: string | null;
  enabled_at: string | null;
}

export interface Entitlement {
  feature: string;
  seats: number | null; // null = unlimited within the tenant
  expires_at: string | null;
}

export interface CustomerDetail {
  customer: {
    pk: number;
    name: string;
    additional_prompt: string | null;
    logo_url: string | null;
    max_message_limit: number | null;
    max_users: number | null;
    database?: { host: string | null; database: string | null };
  };
  flags: Flag[];
  entitlements: Entitlement[];
}

export interface AdminUser {
  pk: number;
  id: string;
  email: string;
  role: string;
  base_message_limit: number | null;
  monthly_message_count: number | null;
  expiration_date: string | null;
  disabled: boolean;
  user_flags: string[];
  // The last day the user can work (expiration_date is the first blocked
  // day); null = no end date.
  last_day?: string | null;
  // The latest welcome email; null when none was ever sent from here or the
  // CLI. send_to is masked by the server.
  welcome: {
    status: 'SENDING' | 'SENT' | 'FAILED';
    at: string;
    send_to: string;
  } | null;
}

export interface NewUserTopic {
  id: string;
  label: string;
  summary: string | null;
}

export interface NewUserOptions {
  customer: { pk: number; name: string; database?: string | null };
  default_message_limit: number;
  max_message_limit: number | null;
  max_users: number | null;
  user_count: number;
  topics: NewUserTopic[];
  // Loadable tables with no topic label (item and party lists): granted with
  // any topic choice.
  supporting_count: number;
  can_choose_topics: boolean;
  last_company: string | null;
}

export interface WelcomeInput {
  send_to: string;
  company: string;
  resend?: boolean;
}

export interface WelcomeResult {
  status: 'sent' | 'refused' | 'failed';
  message: string;
  notes: string[];
}

export interface CreatedUser {
  user: { pk: number; id: string; email: string };
  password: string; // shown once; the server keeps only its hash
  welcome: WelcomeResult | null;
}

export interface Usage {
  days: number;
  questions: number;
  sessions: number;
  cache_ratio: number;
  avg_ms: number;
  report_sends: number;
  reports_sent: number;
  chat_cost_usd?: number; // SUPER_ADMIN only
  features_cost_usd?: number; // dashboards, anomalies, titles, starters...
  report_cost_usd?: number;
  total_cost_usd?: number;
  openrouter_cost_usd?: number; // total_cost_usd split by who was paid
  anthropic_cost_usd?: number; // the Claude Console credit
}

export interface ConversationElement {
  thread_id: string;
  type: 'plotly' | 'image';
  name: string | null;
  mime: string | null;
  url: string | null;
  createdAt: string | null;
}

// An excerpt with the ranges to highlight inside it ([start, end) pairs).
export interface Snippet {
  text: string;
  highlights: [number, number][];
  kind: 'question' | 'answer';
}

export interface ThreadSummary {
  id: string;
  name: string | null; // the thread's title
  created_at: string;
  email: string;
  user_id: string;
  customer_pk: number;
  customer_name: string;
  question_count: number;
  last_activity: string | null; // the latest question
  has_charts?: boolean;
  // Why it matched a search, or the last question when there is no search.
  snippet?: Snippet | null;
  title_highlights?: [number, number][];
}

export interface ThreadSearch {
  customerPk?: number | null; // null/undefined = every company in scope
  userId?: string;
  threadId?: string; // one conversation (a shared link)
  q?: string;
  dateFrom?: string; // YYYY-MM-DD, Cairo
  dateTo?: string;
  sort?: 'recent' | 'newest' | 'questions';
  offset?: number;
  limit?: number;
}

export interface ThreadPage {
  items: ThreadSummary[];
  total: number;
  has_more: boolean;
  offset: number;
  terms: string[];
}

export interface Conversation {
  id: string;
  asked_at: string;
  thread_id: string;
  email: string;
  question: string;
  answer: string | null;
  python_code: string | null;
  elements: ConversationElement[];
  kind: 'question' | 'notice'; // 'notice' = a system message, not something asked
  // Opened from a search: whether this question or its answer has the words.
  match?: boolean;
  question_highlights?: [number, number][];
}

export interface AuditEntry {
  id: number;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  target_customer_pk: number | null;
  target_user_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

async function jsonFetch(url: string, init?: RequestInit) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiAdminMe(): Promise<AdminMe> {
  return jsonFetch('/api/admin/me');
}

export async function apiAdminPlatformSettings(): Promise<{
  settings: PlatformSetting[];
  provisioned_slots: number;
}> {
  return jsonFetch('/api/admin/platform/settings');
}

export async function apiAdminSetPlatformSetting(
  key: string,
  value: number
): Promise<{ key: string; value: number; previous: number; applies: string }> {
  return jsonFetch(`/api/admin/platform/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ value })
  });
}

export async function apiAdminCustomers(): Promise<{ items: CustomerRow[] }> {
  return jsonFetch('/api/admin/customers');
}

export async function apiAdminCustomer(pk: number): Promise<CustomerDetail> {
  return jsonFetch(`/api/admin/customers/${pk}`);
}

export async function apiAdminPatchCustomer(
  pk: number,
  body: {
    additional_prompt?: string;
    max_message_limit?: number;
    max_users?: number;
  }
): Promise<{ updated: boolean }> {
  return jsonFetch(`/api/admin/customers/${pk}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminUsers(
  customerPk: number
): Promise<{ items: AdminUser[]; last_company?: string | null }> {
  return jsonFetch(`/api/admin/users?customer_pk=${customerPk}`);
}

export async function apiAdminPatchUser(
  userPk: number,
  body: {
    base_message_limit?: number;
    expiration_date?: string;
    last_day?: string; // the last day the user can work; '' clears
    disabled?: boolean;
    english_only?: boolean;
  }
): Promise<{ updated: boolean }> {
  return jsonFetch(`/api/admin/users/${userPk}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminNewUserOptions(
  customerPk: number
): Promise<NewUserOptions> {
  return jsonFetch(`/api/admin/users/new-options?customer_pk=${customerPk}`);
}

export async function apiAdminCreateUser(body: {
  customer_pk: number;
  email: string;
  base_message_limit?: number;
  last_day?: string;
  topics: string[] | null; // null = every topic (the tenant default)
  welcome: WelcomeInput | null;
  english_only: boolean;
}): Promise<CreatedUser> {
  return jsonFetch('/api/admin/users', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminSendWelcome(
  userPk: number,
  body: WelcomeInput
): Promise<WelcomeResult> {
  return jsonFetch(`/api/admin/users/${userPk}/welcome-email`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminSetFlag(body: {
  customer_pk: number;
  feature: string;
  enabled: boolean;
  user_id?: string | null;
}): Promise<{ ok: boolean }> {
  return jsonFetch('/api/admin/flags', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminSetEntitlement(
  customerPk: number,
  body: { feature: string; seats?: number | null; expires_at?: string | null }
): Promise<{ ok: boolean }> {
  return jsonFetch(`/api/admin/entitlements/${customerPk}`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminDeleteEntitlement(
  customerPk: number,
  feature: string
): Promise<{ deleted: boolean }> {
  return jsonFetch(`/api/admin/entitlements/${customerPk}/${feature}`, {
    method: 'DELETE'
  });
}

export interface MemoryRow {
  id: number;
  fact: string;
  created_by_email: string | null;
  created_at: string;
  check_kind: string | null;
  checked_at: string | null;
  contradicted_at: string | null;
  last_used_at: string | null;
  baseline_count: number | null;
}

// A fact a user suggested, waiting for someone with Signals memory
// (memory_admin) or BuduCloud to approve it.
export interface MemoryProposal {
  id: number;
  fact: string;
  proposed_by_email: string | null;
  created_at: string;
  segment_key: string | null;
}

export async function apiAdminMemory(
  customerPk: number
): Promise<{ items: MemoryRow[]; proposals?: MemoryProposal[] }> {
  return jsonFetch(`/api/admin/memory?customer_pk=${customerPk}`);
}

export async function apiAdminReviewMemory(
  customerPk: number,
  proposalId: number,
  decision: 'approve' | 'reject'
): Promise<{ status: string }> {
  return jsonFetch(
    `/api/admin/memory/proposals/${proposalId}/${decision}?customer_pk=${customerPk}`,
    { method: 'POST' }
  );
}

export async function apiAdminDeleteMemory(
  customerPk: number,
  memoryId: number
): Promise<{ ok: boolean }> {
  return jsonFetch(`/api/admin/memory/${memoryId}?customer_pk=${customerPk}`, {
    method: 'DELETE'
  });
}

export async function apiAdminUsage(
  customerPk: number,
  days = 30
): Promise<Usage> {
  return jsonFetch(`/api/admin/usage?customer_pk=${customerPk}&days=${days}`);
}

export async function apiAdminThreads(
  search: ThreadSearch,
  signal?: AbortSignal
): Promise<ThreadPage> {
  const qs = new URLSearchParams({
    limit: String(search.limit ?? 30),
    offset: String(search.offset ?? 0),
    sort: search.sort ?? 'recent'
  });
  if (search.customerPk != null)
    qs.set('customer_pk', String(search.customerPk));
  if (search.userId) qs.set('user_id', search.userId);
  if (search.threadId) qs.set('thread_id', search.threadId);
  if (search.q?.trim()) qs.set('q', search.q.trim());
  if (search.dateFrom) qs.set('date_from', search.dateFrom);
  if (search.dateTo) qs.set('date_to', search.dateTo);
  return jsonFetch(`/api/admin/threads?${qs.toString()}`, { signal });
}

// With threadId: the whole conversation in order, each question carrying its
// own charts. Without: the customer's latest questions across all threads.
export async function apiAdminConversations(
  customerPk: number,
  opts: { threadId?: string; userId?: string; limit?: number; q?: string } = {}
): Promise<{ items: Conversation[] }> {
  const qs = new URLSearchParams({
    customer_pk: String(customerPk),
    limit: String(opts.limit ?? 50)
  });
  if (opts.threadId) qs.set('thread_id', opts.threadId);
  if (opts.userId) qs.set('user_id', opts.userId);
  if (opts.q?.trim()) qs.set('q', opts.q.trim());
  return jsonFetch(`/api/admin/conversations?${qs.toString()}`);
}

// ── Usage across every company ──────────────────────────────────────────────

export type UserStatus =
  | 'active' // seen in the last 7 days
  | 'idle' // 8-30 days
  | 'dormant' // over 30 days
  | 'never'
  | 'disabled'
  | 'expired';

export interface UsageUserRow {
  pk: number;
  id: string;
  email: string;
  customer_pk: number;
  customer_name: string;
  role: string;
  disabled: boolean;
  expiration_date: string | null;
  monthly_message_count: number | null;
  base_message_limit: number | null;
  created_at: string | null;
  questions: number; // in the window
  questions_total: number;
  conversations: number;
  conversations_total: number;
  last_question_at: string | null;
  last_question: { text: string; thread_id: string; at: string | null } | null;
  last_seen_at: string | null; // a visit, a question or a sign-in
  last_client: 'web' | 'mobile' | 'other' | null;
  clients: string[];
  active_days: number; // days with a recorded visit, in the window
  last_login_at: string | null;
  last_login_method: string | null;
  logins: number;
  avg_answer_ms: number | null;
  cost_usd?: number; // SUPER_ADMIN only
  status: UserStatus;
}

export interface UsageOverview {
  days: number;
  first_day: string;
  totals: {
    users: number;
    active_1d: number;
    active_7d: number;
    active_30d: number;
    questions: number;
    conversations: number;
    never: number;
    dormant: number;
    companies: number;
    cost_usd?: number;
  };
  series: { day: string; questions: number; active_users: number }[];
  items: UsageUserRow[];
  tracking_since: string | null; // visits and sign-ins recorded from this day
}

export interface UsageUserDetail {
  user: {
    pk: number;
    id: string;
    email: string;
    customer_pk: number;
    customer_name: string;
  };
  days: number;
  series: { day: string; questions: number; visits: Record<string, number> }[];
  logins: {
    created_at: string;
    method: string;
    client: string;
    ip?: string | null; // SUPER_ADMIN only
    user_agent?: string | null;
  }[];
}

export async function apiAdminUsageOverview(
  days = 30,
  customerPk?: number
): Promise<UsageOverview> {
  const qs = new URLSearchParams({ days: String(days) });
  if (customerPk !== undefined) qs.set('customer_pk', String(customerPk));
  return jsonFetch(`/api/admin/usage/overview?${qs.toString()}`);
}

export async function apiAdminUsageUser(
  userPk: number,
  days = 90
): Promise<UsageUserDetail> {
  return jsonFetch(`/api/admin/usage/users/${userPk}?days=${days}`);
}

const LOGIN_METHOD: Record<string, string> = {
  password: 'password',
  jwt: 'from the ERP',
  two_step: 'password + code',
  header: 'header',
  oauth: 'single sign-on'
};

export function loginMethodLabel(method: string | null): string {
  return method ? LOGIN_METHOD[method] ?? method : '—';
}

// "3 min ago", "yesterday", "12 days ago"; the exact time goes in a title.
export function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const sec = Math.max(0, (Date.now() - then) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  const days = Math.floor(sec / 86400);
  if (days === 1) return 'yesterday';
  if (days < 60) return `${days} days ago`;
  return `${Math.floor(days / 30)} months ago`;
}

// ── Spend & volume ──────────────────────────────────────────────────────────
// Money fields are present for BuduCloud only; a partner gets the volume.

export interface SpendMonth {
  month: string; // YYYY-MM
  questions: number;
  conversations: number;
  askers: number;
  report_sends: number;
  reports_sent: number;
  avg_answer_ms: number | null;
  cost_usd?: number;
  anthropic_usd?: number;
  openrouter_usd?: number;
  chat_usd?: number;
  features_usd?: number;
  reports_usd?: number;
  cost_per_question?: number | null;
  cache_ratio?: number | null;
}

export interface SpendCompany {
  customer_pk: number; // -1 = our tests & evals, 0 = no tenant recorded
  customer_name: string;
  internal: boolean;
  questions: number;
  askers: number;
  cost_usd?: number;
  anthropic_usd?: number;
  openrouter_usd?: number;
  months: { month: string; questions: number; cost_usd?: number }[];
}

export interface Spend {
  months: SpendMonth[];
  companies: SpendCompany[];
  heatmap: { dow: number; hour: number; questions: number }[]; // dow 1 = Monday
  current: {
    month: string;
    day: number;
    days_in_month: number;
    questions: number;
    questions_at_pace: number;
    cost_usd?: number;
    cost_at_pace?: number;
  };
  totals: {
    questions: number;
    conversations: number;
    report_sends: number;
    cost_usd?: number;
    anthropic_usd?: number;
    openrouter_usd?: number;
    chat_usd?: number;
    features_usd?: number;
    reports_usd?: number;
  };
  cost_tracked_since?: string | null;
  features?: {
    endpoint: string;
    label: string;
    calls: number;
    cost_usd: number;
    months: { month: string; cost_usd: number }[];
  }[];
  models?: { model: string; provider: string; calls: number; cost_usd: number }[];
}

export async function apiAdminSpend(months = 6): Promise<Spend> {
  return jsonFetch(`/api/admin/spend?months=${months}`);
}

export async function apiAdminAudit(
  customerPk?: number,
  limit = 100
): Promise<{ items: AuditEntry[] }> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (customerPk !== undefined) qs.set('customer_pk', String(customerPk));
  return jsonFetch(`/api/admin/audit?${qs.toString()}`);
}

// ── Reports tab ─────────────────────────────────────────────────────────────

export interface ReportSend {
  id: number;
  subscription_id: number;
  status: 'SENT' | 'FAILED' | 'EMPTY' | string;
  sent_at: string | null;
  triggered_by: string | null;
  to_email: string | null;
  has_email: boolean;
  // A plain sentence for a send that did not go out; null when it did.
  why: string | null;
  llm_cost_usd?: number | null; // SUPER_ADMIN only
}

export interface ReportRow {
  id: number;
  title: string;
  question: string;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | string;
  hour_local: number;
  minute_local: number;
  weekday: number | null; // Monday = 0
  day_of_month: number | null;
  timezone: string | null;
  recipient_email: string;
  active: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_status: string | null;
  consecutive_failures: number;
  created_by: string | null;
  created_at: string;
  owner_email: string | null;
  // Failed, overdue, or switched off by failures: someone has to look.
  attention: boolean;
  why: string | null;
  sends: ReportSend[];
}

export async function apiAdminReports(
  customerPk: number
): Promise<{ items: ReportRow[]; attention: number }> {
  return jsonFetch(`/api/admin/reports?customer_pk=${customerPk}`);
}

export function reportEmailUrl(customerPk: number, sendId: number): string {
  return `/api/admin/reports/sends/${sendId}/email?customer_pk=${customerPk}`;
}

const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

export function describeSchedule(r: ReportRow): string {
  const time = `${String(r.hour_local).padStart(2, '0')}:${String(
    r.minute_local
  ).padStart(2, '0')}`;
  const zone = r.timezone ? ` (${r.timezone})` : '';
  if (r.frequency === 'WEEKLY')
    return `Every ${WEEKDAYS[r.weekday ?? 0]} at ${time}${zone}`;
  if (r.frequency === 'MONTHLY')
    return `Monthly on day ${r.day_of_month ?? 1} at ${time}${zone}`;
  return `Daily at ${time}${zone}`;
}

// ── display helpers ──────────────────────────────────────────────────────────

export const FEATURE_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  dashboard_admin: 'Dashboard admin',
  anomaly_detection: 'Anomalies',
  anomaly_admin: 'Anomalies admin',
  anomaly_user_alerts: 'My alerts',
  memory_admin: 'Signals memory admin',
  web_search: 'Web search',
  scheduled_reports: 'Scheduled reports',
  scheduled_reports_pro: 'Scheduled reports — Pro',
  english_only: 'English only'
};

// A setting of the user, not a plan feature: chosen on Create user and on the
// user's row, never in the grantable list (admin_api.ENGLISH_ONLY).
export const ENGLISH_ONLY = 'english_only';

// What each flag actually unlocks — an admin toggling these should never have
// to guess the difference between a feature and its admin variant.
export const FEATURE_HELP: Record<string, string> = {
  dashboard: 'See the Dashboard page: auto-built widgets over their own data.',
  dashboard_admin:
    'Additionally edit the company-wide default dashboard everyone sees — regenerate it, add or remove widgets, change chart types. Without this a user can only rearrange their own copy.',
  anomaly_detection:
    'See the Anomalies page and review what the nightly detection flagged.',
  anomaly_admin:
    'Additionally create, edit and delete the detection rules, and trigger a detection run on demand. Without this a user can only review, not change what is monitored.',
  anomaly_user_alerts:
    'Let any user create their own alert by describing it in a sentence, in Arabic or English — "tell me if daily sales for the Maadi branch drop more than 20%". Alerts are private to whoever created them, capped at 5 each, and run in the same nightly check as the automatic rules. Does not require Anomalies admin.',
  memory_admin:
    'Add, approve and remove the facts Signals remembers about the company. Everyone else can only suggest one; it applies to the whole company after someone with this permission approves it.',
  web_search:
    'Let answers pull public market context (FX rates, commodity prices, seasons). Customer data never leaves the tenant — queries are entity-guarded and audited.',
  scheduled_reports:
    'Subscribe questions to be answered automatically and emailed on a schedule (3 reports, weekly or monthly).',
  scheduled_reports_pro:
    'Raise that to 20 reports and unlock DAILY delivery — the paid tier.',
  english_only:
    'For a user who reads no Arabic: skills and the account statement show English names only. Answers follow the language of the question.'
};

export function featureLabel(feature: string): string {
  return FEATURE_LABEL[feature] ?? feature;
}

export function featureHelp(feature: string): string | null {
  return FEATURE_HELP[feature] ?? null;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return iso;
  }
}

// ── Data tab ────────────────────────────────────────────────────────────────

export interface DataTable {
  table_name: string;
  approx_rows: number | null;
  df_id: number | null;
  description: string | null;
  missing_table?: boolean;
}

export interface EtlConfig {
  table_name: string;
  url: string | null;
  enabled: boolean;
  last_update_date: string | null;
  allow_replace: boolean | null;
  resync_days: number | null;
  date_field: string | null;
}

export interface SyncAudit {
  table_name: string;
  window_start: string | null;
  window_end: string | null;
  api_count: number | null;
  db_count: number | null;
  matched: boolean | null;
  run_at: string | null;
}

export interface DataOverview {
  tables: DataTable[];
  etl: EtlConfig[]; // SUPER_ADMIN only (api_config headers may hold ERP keys)
  sync_audit: SyncAudit[];
  described: number;
  total: number;
  supports_descriptions: boolean; // false on tenants with an older dfs schema
  dfs_available: boolean; // false on tenants with no dfs table at all
}

export async function apiAdminData(customerPk: number): Promise<DataOverview> {
  return jsonFetch(`/api/admin/data?customer_pk=${customerPk}`);
}

export async function apiAdminSetDescription(
  customerPk: number,
  body: { table_name: string; description: string }
): Promise<{ ok: boolean; df_id: number }> {
  return jsonFetch(`/api/admin/data/description?customer_pk=${customerPk}`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export interface GrantableTable {
  df_id: number;
  name: string;
  description: string | null;
  registered: boolean;
  /** The nightly ETL keeps this topic up to date. */
  enabled: boolean;
  /** Signals Chat may load it. Independent of `enabled`: turning this off
   *  removes the topic from conversations and leaves the ETL and the stored
   *  data exactly where they are. */
  sandbox_enabled: boolean;
  /** False on a tenant whose database predates the flag, where the two cannot
   *  be told apart yet. */
  sandbox_supported: boolean;
  present: boolean;
  /** False when granting it would grant nothing: the sandbox loads only the
   *  tables that are described, switched on in the sync, available to the
   *  chat, and really there. */
  usable: boolean;
}

export interface VisibilityOverview {
  /** The hand-picked list per user, whether or not it is in force. */
  grants: Record<string, number[]>;
  /** The users whose list IS in force. Everyone else inherits the default. */
  custom: string[];
  /** False on a tenant the users_table_access migration has not reached. */
  custom_supported: boolean;
  tables: GrantableTable[];
}

export async function apiAdminVisibility(
  customerPk: number
): Promise<VisibilityOverview> {
  return jsonFetch(`/api/admin/data/visibility?customer_pk=${customerPk}`);
}

export async function apiAdminSetTableAccessMode(
  customerPk: number,
  body: { user_id: string; custom: boolean }
): Promise<{ ok: boolean }> {
  return jsonFetch(`/api/admin/data/table-access?customer_pk=${customerPk}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminSetChatAvailability(
  customerPk: number,
  body: { table_name: string; available: boolean }
): Promise<{ ok: boolean; catalog_version: number }> {
  return jsonFetch(`/api/admin/data/chat-availability?customer_pk=${customerPk}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}

export async function apiAdminSetVisibility(
  customerPk: number,
  body: { user_id: string; df_id: number; visible: boolean }
): Promise<{ ok: boolean }> {
  return jsonFetch(`/api/admin/data/visibility?customer_pk=${customerPk}`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
}
